import { useCallback, useEffect, useRef, useState } from 'react';
import { ProfileCostEstimate } from '@features/Admin/Billing/ui/ProfileCostEstimate';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  IconArrowLeft,
  IconBook2,
  IconMicrophone,
  IconPlayerPlay,
  IconRobot,
  IconSettings,
  IconWaveSine,
} from '@tabler/icons-react';
import { useAppSelector } from '@shared/store';
import { useDesignVariant } from '@shared/designVariants/DesignVariantProvider';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { useAskPageContext } from '@shared/designVariants/useAskPageContext';
import {
  useAgentProfileListControllerAgentProfileListForUsersQuery,
  useCampaignControllerCampaignQuery,
  useCampaignAgentListControllerListQuery,
  useCompanyControllerGetCompanyQuery,
  useUserMeControllerUserMeQuery,
  type AgentDto,
  type AgentProfileResponseDto,
} from '@shared/services/apiService';
import { ManageAgent } from '@pages/Admin/Main/Agent/ui/components/ManageAgent';
import { TestingAgentPopup } from '@widgets/Popup/TestingAgentPopup/TestingAgentPopup';
import OngoingCallsTab from '@pages/Admin/Campaign/ui/SidebarTabs/OngoingCallsTab';
import { CampaignResources } from './CampaignResources';
import { AgentConfigurationChecks } from './AgentConfigurationChecks';
import { AgentJobEditor } from './AgentJobEditor';
import { AgentStudioPanels } from './AgentStudioPanels';
import { V4StudioPage } from './V4StudioPage';
import { CopyJobAction } from './CopyJobAction';
import { useWorkspaceDraftGuard } from './useWorkspaceDraftGuard';
import { useWorkspaceScope } from './useWorkspaceScope';
import { workspaceAgentPath } from './workspacePaths';
import type { WorkspaceModule } from '@shared/designVariants/catalog';

export const AgentStudioPage = () => {
  const { campaignId = '', agentId = '' } = useParams();
  const scope = useWorkspaceScope();
  const { variant } = useDesignVariant();
  const [query] = useSearchParams();
  if (variant === 'v4' && agentId !== 'new') return <V4StudioPage />;
  return (
    <AgentStudioSession
      key={`${scope}:${campaignId}:${agentId}:${agentId === 'new' ? query.get('setup') || '' : ''}`}
      campaignId={campaignId}
      agentId={agentId}
    />
  );
};

const resourceSections: WorkspaceModule[] = [
  'Knowledge base',
  'Audiences',
  'Tools',
  'Webhooks',
  'Phone numbers',
  'WhatsApp',
  'Evaluations',
  'Analytics',
];
const AgentStudioSession = ({ campaignId, agentId }: { campaignId: string; agentId: string }) => {
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const { variant } = useDesignVariant();
  const workspace = useDesignWorkspace();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const campaignQuery = useCampaignControllerCampaignQuery({ id: campaignId });
  const agentsQuery = useCampaignAgentListControllerListQuery({ id: campaignId });
  const companyQuery = useCompanyControllerGetCompanyQuery(
    { id: user?.companyId || '' },
    { skip: !user?.companyId, refetchOnMountOrArgChange: true },
  );
  const profilesQuery = useAgentProfileListControllerAgentProfileListForUsersQuery(
    { filter: undefined },
    { refetchOnMountOrArgChange: true },
  );
  const meQuery = useUserMeControllerUserMeQuery();
  const me = meQuery.currentData;
  const reloadProfiles = profilesQuery.refetch;
  const reloadAgents = agentsQuery.refetch;
  const reloadCampaign = campaignQuery.refetch;
  const [tab, setTab] = useState('agent');
  const [resource, setResource] = useState<WorkspaceModule>('Knowledge base');
  const [settings, setSettings] = useState(false);
  const [initialJob, setInitialJob] = useState<string | undefined>(params.get('job') || undefined);
  const [jobDirty, setJobDirty] = useState(false);
  const [agentDirty, setAgentDirty] = useState(false);
  const [resourceDirty, setResourceDirty] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [test, setTest] = useState(false);
  const [created, setCreated] = useState(false);
  const active = useRef(true);
  const currentEpoch = useRef(epoch);
  const visible = useRef(!workspace.module);
  currentEpoch.current = epoch;
  visible.current = !workspace.module;
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const campaign = campaignQuery.currentData;
  const agents = campaign?.squad?.agents ?? agentsQuery.currentData;
  const agent = agents?.find((row) => row.id === agentId);
  const profile = profilesQuery.currentData?.find((row) => row.id === agent?.profileId);
  useAskPageContext(
    campaign && campaign.companyId === user?.companyId && !campaignQuery.isError
      ? {
          page: 'Agent Studio',
          campaign: { id: campaign.id, title: campaign.title },
          ...(!agentsQuery.isError && agent && { agent: { id: agent.id, name: agent.name } }),
          ...(!profilesQuery.isError &&
            profile &&
            me?.companyId === user?.companyId && { job: { id: profile.id, name: profile.name } }),
        }
      : null,
  );
  const company = companyQuery.currentData;
  const creating = agentId === 'new';
  const canManage =
    user?.role !== 'viewer' &&
    me?.companyId === user?.companyId &&
    !!me?.privileges.includes('agent.manage');
  const canEditAgent = canManage && campaign?.status === 'inactive';
  const discard = useCallback(() => {
    setJobDirty(false);
    setAgentDirty(false);
    setResourceDirty(false);
    setEpoch((value) => value + 1);
  }, []);
  const { request, dialog } = useWorkspaceDraftGuard(
    jobDirty || agentDirty || resourceDirty,
    discard,
  );
  const loadJob = useCallback(
    async (id: string) => {
      const response = await reloadProfiles();
      if (response.error) throw new Error('Unable to reload the current Job library.');
      return response.data?.find((row) => row.id === id);
    },
    [reloadProfiles],
  );
  const loadCurrentJob = useCallback(() => loadJob(profile?.id || ''), [loadJob, profile?.id]);
  const refresh = useCallback(() => {
    void reloadProfiles();
    void reloadAgents();
    void reloadCampaign();
  }, [reloadProfiles, reloadAgents, reloadCampaign]);
  const handleCopiedJob = useCallback(
    (copy: AgentProfileResponseDto) => {
      if (!active.current || !visible.current || currentEpoch.current !== epoch) return;
      void reloadProfiles();
      request(() => {
        setInitialJob(copy.id);
        setSettings(true);
        setTab('agent');
      });
    },
    [reloadProfiles, request, epoch],
  );
  const copiedForCreation = useCallback(
    async (copy: AgentProfileResponseDto) => {
      await reloadProfiles();
      if (!active.current || !visible.current || currentEpoch.current !== epoch) return;
      setInitialJob(copy.id);
    },
    [reloadProfiles, epoch],
  );
  const afterCreation = async () => {
    if (!active.current || !visible.current || currentEpoch.current !== epoch) return;
    setCreated(true);
    setAgentDirty(false);
    const [updatedCampaign, updatedAgents] = await Promise.all([reloadCampaign(), reloadAgents()]);
    if (!active.current || !visible.current || currentEpoch.current !== epoch) return;
    const savedAgents = updatedCampaign.data?.squad?.agents ?? updatedAgents.data;
    const added = savedAgents?.filter((row) => !agents?.some((old) => old.id === row.id));
    if (added?.length === 1)
      navigate(workspaceAgentPath(campaignId, added[0].id), { replace: true });
  };
  const closeCreation = () => {
    setAgentDirty(false);
    navigate('/admin/campaign');
  };
  const loading =
    (!campaign && campaignQuery.isFetching) || (!agents && agentsQuery.isFetching) || !me;
  if (meQuery.isError)
    return (
      <Alert severity="error">
        Current permissions could not be loaded.{' '}
        <Button onClick={() => void meQuery.refetch()}>Retry permissions</Button>
      </Alert>
    );
  if (loading) return <CircularProgress aria-label="Loading agent workspace" />;
  if (
    !campaign ||
    campaignQuery.isError ||
    campaign.companyId !== user?.companyId ||
    agentsQuery.isError ||
    !agents ||
    (!creating && !agent)
  )
    return (
      <Stack gap={2}>
        <Alert severity="error">
          This campaign or agent is unavailable in your current company.
        </Alert>
        <Button onClick={() => workspace.open('Agents')}>Back to agents</Button>
        <Button onClick={refresh}>Retry current data</Button>
      </Stack>
    );
  const selectedJob = initialJob
    ? profilesQuery.currentData?.find((row) => row.id === initialJob)
    : undefined;
  const blocked = !canEditAgent
    ? 'Agent changes require management permission and an inactive campaign.'
    : companyQuery.isError || !company
      ? 'Current company settings are unavailable.'
      : undefined;
  const nativeEditor = (current: AgentDto | null) => (
    <ManageAgent
      key={`${epoch}:${current?.id || 'new'}:${initialJob || ''}`}
      presentation="panel"
      guided={!current}
      allowJobDraft={!current && variant !== 'main'}
      creationSessionId={params.get('setup') || 'legacy'}
      canContinueCreate={() =>
        active.current && visible.current && currentEpoch.current === epoch && canEditAgent
      }
      agents={agents}
      agent={current}
      initialProfileId={initialJob}
      campaignName={campaign.title}
      squadId={campaign.squad?.id}
      voiceData={company?.voices}
      transcriberData={company?.transcribers}
      llmProviders={company?.llmProviders}
      isCompanyLoading={!company && companyQuery.isFetching}
      isCompanyError={companyQuery.isError}
      blockedReason={blocked}
      onDirtyChange={setAgentDirty}
      onCreated={afterCreation}
      onSaved={() => {
        setAgentDirty(false);
        setSettings(false);
        setInitialJob(undefined);
        refresh();
      }}
      onClose={current ? () => request(() => setSettings(false)) : closeCreation}
    />
  );
  return (
    <Stack
      component="section"
      aria-label={creating ? 'Create assistant workspace' : 'Agent studio workspace'}
      gap={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
        <Box>
          <Button
            size="small"
            startIcon={<IconArrowLeft size={16} />}
            onClick={() => workspace.open('Agents')}>
            Back to {variant === 'v5' ? 'assistants' : 'agents'}
          </Button>
          <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 2 }}>
            <Avatar src={agent?.avatarUrl || undefined}>
              <IconRobot />
            </Avatar>
            <Box>
              <Typography component="h1" variant="h3">
                {creating ? 'Create an assistant' : agent?.name}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {campaign.title} · {campaign.type} · {campaign.status}
              </Typography>
              {!creating && user?.companyId && (
                <ProfileCostEstimate
                  key={`${agent?.id}:${agent?.updatedAt}`}
                  companyId={user.companyId}
                  agentId={agentId}
                />
              )}
            </Box>
          </Stack>
        </Box>
        {!creating && (
          <Stack direction="row" gap={1} alignItems="start" flexWrap="wrap">
            <AgentConfigurationChecks campaign={campaign} agents={[agent!]} />
            <Button
              variant="contained"
              startIcon={<IconPlayerPlay size={16} />}
              disabled={user?.role === 'viewer' || !me?.privileges.includes('call.test')}
              onClick={() => request(() => setTest(true))}>
              Test agent
            </Button>
          </Stack>
        )}
      </Stack>
      {creating ? (
        <>
          {created ? (
            <Alert severity="success">
              The assistant was saved. Open its current campaign agents to continue.
            </Alert>
          ) : !canEditAgent || (!campaign.squad && agents.length > 0) ? (
            <Alert severity="warning">
              {blocked ||
                'This campaign already has a single agent. Configure a squad before adding another assistant.'}
            </Alert>
          ) : initialJob && (!selectedJob || profilesQuery.isFetching) ? (
            <Alert severity="info">
              {profilesQuery.isFetching
                ? 'Loading the selected job…'
                : 'The selected job is no longer available. Choose another job from the library.'}
            </Alert>
          ) : selectedJob?.type === 'customer-template' ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack gap={2}>
                <Typography variant="h5">Start from {selectedJob.name}</Typography>
                <Typography>{selectedJob.prompt}</Typography>
                <Alert severity="info">
                  Create a custom copy with the complete workflow before assigning this protected
                  template.
                </Alert>
                <CopyJobAction
                  profile={selectedJob}
                  disabled={!canManage}
                  loadCurrent={() => loadJob(selectedJob.id)}
                  onCopied={(copy) => void copiedForCreation(copy)}
                />
              </Stack>
            </Paper>
          ) : (
            <Box
              component="table"
              sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>{nativeEditor(null)}</tr>
              </tbody>
            </Box>
          )}
          {created &&
            agents.map((row) => (
              <Button key={row.id} onClick={() => navigate(workspaceAgentPath(campaignId, row.id))}>
                Open {row.name}
              </Button>
            ))}
        </>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(_, value) => {
              if (resourceDirty) request(() => setTab(value));
              else setTab(value);
            }}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Agent workspace sections">
            <Tab
              value="agent"
              label={
                variant === 'v2'
                  ? 'Assistant'
                  : variant === 'v3'
                    ? 'Studio'
                    : variant === 'v1'
                      ? 'Agent setup'
                      : 'Agent'
              }
            />
            <Tab value="workflow" label="Workflow" />
            <Tab value="resources" label="Resources" />
            <Tab value="history" label="History" />
          </Tabs>
          <Box sx={{ display: ['agent', 'workflow'].includes(tab) ? 'block' : 'none' }}>
            <Box sx={{ display: tab === 'agent' ? 'block' : 'none', mb: 3 }}>
              {['main', 'v1', 'v2'].includes(variant) && (
                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                  {[
                    [
                      'Voice & language',
                      agent?.voice?.name ||
                        company?.voices?.find((v) => v.id === agent?.voiceId)?.name ||
                        agent?.voiceId ||
                        'Not configured',
                      (agent?.languages || []).join(', '),
                      <IconWaveSine key="voice" />,
                    ],
                    [
                      'Transcription',
                      agent?.transcriber?.provider ||
                        company?.transcribers?.find((v) => v.id === agent?.transcriberId)
                          ?.provider ||
                        agent?.transcriberId ||
                        'Not configured',
                      agent?.transcriber?.model,
                      <IconMicrophone key="stt" />,
                    ],
                    [
                      'Language model',
                      agent?.llmProvider?.title ||
                        company?.llmProviders?.find((v) => v.id === agent?.llmProviderId)?.title ||
                        agent?.llmProviderId ||
                        'Not configured',
                      agent?.llmModel?.title || agent?.llmCustomModelKey || agent?.llmModelId || '',
                      <IconBook2 key="llm" />,
                    ],
                    [
                      'Job',
                      profile?.name || 'No reusable job',
                      profile?.type || '',
                      <IconBook2 key="job" />,
                    ],
                  ].map(([label, value, detail, icon]) => (
                    <Paper
                      key={String(label)}
                      variant="outlined"
                      sx={{ p: 2, flex: 1, minWidth: 0 }}>
                      <Stack gap={1}>
                        {icon}
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography fontWeight={600}>{value}</Typography>
                        <Typography variant="caption">{detail}</Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
              <Button
                sx={{ mt: 2 }}
                startIcon={<IconSettings size={17} />}
                onClick={() => {
                  if (settings) request(() => setSettings(false));
                  else setSettings(true);
                }}>
                {settings ? 'Hide agent settings' : 'Voice, model and agent settings'}
              </Button>
              {settings && (
                <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mt: 2 }}>
                  {nativeEditor(agent!)}
                </Paper>
              )}
            </Box>
            <Box
              sx={{
                display: tab === 'agent' && ['v3', 'v4', 'v5'].includes(variant) ? 'grid' : 'block',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  lg:
                    variant === 'v3'
                      ? 'minmax(0, 1.6fr) minmax(220px, 0.9fr) minmax(220px, 0.9fr)'
                      : 'minmax(0, 1fr) 290px',
                },
                gap: 2,
                alignItems: 'start',
              }}>
              <Box sx={{ minWidth: 0 }}>
                {profilesQuery.isFetching && !profilesQuery.currentData ? (
                  <CircularProgress aria-label="Loading job" />
                ) : profilesQuery.isError ? (
                  <Alert severity="error">
                    The current Job library is unavailable.{' '}
                    <Button onClick={() => void reloadProfiles()}>Retry</Button>
                  </Alert>
                ) : profile ? (
                  <AgentJobEditor
                    key={`${profile.id}:${epoch}`}
                    profile={profile}
                    workflow={tab === 'workflow'}
                    plainInstructions={variant === 'v3'}
                    canManage={canManage}
                    loadCurrent={loadCurrentJob}
                    onDirtyChange={setJobDirty}
                    onUpdated={refresh}
                    onCopied={handleCopiedJob}
                  />
                ) : (
                  <Alert severity="info">
                    Choose a reusable job in agent settings to configure its instructions and
                    workflow.
                  </Alert>
                )}
              </Box>
              {tab === 'agent' && ['v3', 'v4', 'v5'].includes(variant) && (
                <AgentStudioPanels
                  threeColumns={variant === 'v3'}
                  voice={
                    agent?.voice?.name ||
                    company?.voices?.find((v) => v.id === agent?.voiceId)?.name ||
                    agent?.voiceId ||
                    'Not configured'
                  }
                  language={(agent?.languages || []).join(', ') || 'Not configured'}
                  model={
                    agent?.llmModel?.title ||
                    agent?.llmCustomModelKey ||
                    agent?.llmModelId ||
                    'Not configured'
                  }
                  transcriber={
                    agent?.transcriber?.provider ||
                    company?.transcribers?.find((v) => v.id === agent?.transcriberId)?.provider ||
                    agent?.transcriberId ||
                    'Not configured'
                  }
                  canTest={user?.role !== 'viewer' && !!me?.privileges.includes('call.test')}
                  openTest={() => request(() => setTest(true))}
                  configure={() => setSettings(true)}
                  resource={(next) => {
                    setResource(next);
                    setTab('resources');
                  }}
                  workflow={() => setTab('workflow')}
                  campaign={() => workspace.navigate(`/admin/campaign/${campaignId}/edit`)}
                />
              )}
            </Box>
          </Box>
          {tab === 'resources' && (
            <Stack gap={2}>
              <Typography variant="body2" color="text.secondary">
                Resources retain their native campaign scope. Agent assignments are shown by the
                knowledge and tools editors.
              </Typography>
              <TextField
                select
                label="Resource"
                value={resource}
                onChange={(e) =>
                  request(() => {
                    setResource(e.target.value as WorkspaceModule);
                    setResourceDirty(false);
                  })
                }>
                {resourceSections.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <CampaignResources
                key={`${resource}:${epoch}`}
                module={resource}
                campaignId={campaignId}
                onDirtyChange={setResourceDirty}
                requestNavigation={request}
              />
            </Stack>
          )}
          {tab === 'history' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>
                Campaign call history
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Saved calls for {campaign.title}, including all agents and handoffs in this
                campaign.
              </Typography>
              <Box sx={{ height: '70vh', minHeight: 500 }}>
                <OngoingCallsTab
                  campaignId={campaignId}
                  campaignData={campaign}
                  campaignType={campaign.type}
                />
              </Box>
            </Paper>
          )}
        </>
      )}
      {test && agent && (
        <TestingAgentPopup
          open
          onClose={() => setTest(false)}
          agentId={agent.id}
          agentName={agent.name}
          companyHash={campaign.companyId}
          campaignId={campaignId}
          squadId={campaign.squad?.id}
        />
      )}
      {dialog}
    </Stack>
  );
};
